// context/AuthContext.jsx
import React, { createContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

// ✅ Session rules (edit to your liking)
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes idle
const ABSOLUTE_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours max session (even if active)
const CHECK_INTERVAL_MS = 10 * 1000; // check every 10s

// ✅ Storage keys (keep consistent everywhere)
const LS_TOKEN = "token";
const LS_USER = "user";
const LS_SESSION_START = "auth:sessionStart";
const LS_LAST_ACTIVITY = "auth:lastActivity";
const LS_EXPIRES_AT = "auth:expiresAt";

function safeJsonParse(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

// If token is JWT, read exp (seconds) -> ms. If not JWT, returns null.
function getJwtExpMs(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;

  try {
    const payload = JSON.parse(atob(parts[1]));
    if (!payload?.exp) return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // prevent double navigation loops
  const didInitRef = useRef(false);

  const clearSessionStorage = useCallback(() => {
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_SESSION_START);
    localStorage.removeItem(LS_LAST_ACTIVITY);
    localStorage.removeItem(LS_EXPIRES_AT);
  }, []);

  const logout = useCallback(
    (opts = { redirect: true }) => {
      clearSessionStorage();
      setUser(null);
      if (opts.redirect) navigate("/login", { replace: true });
    },
    [clearSessionStorage, navigate]
  );

  const establishSession = useCallback((userData, token) => {
    const now = Date.now();

    // absolute expiry from now
    const absoluteExpiry = now + ABSOLUTE_TIMEOUT_MS;

    // if JWT has exp, pick the earlier of JWT exp and absolute expiry
    const jwtExp = getJwtExpMs(token);
    const expiresAt = jwtExp ? Math.min(jwtExp, absoluteExpiry) : absoluteExpiry;

    localStorage.setItem(LS_TOKEN, token);
    localStorage.setItem(LS_USER, JSON.stringify(userData));
    localStorage.setItem(LS_SESSION_START, String(now));
    localStorage.setItem(LS_LAST_ACTIVITY, String(now));
    localStorage.setItem(LS_EXPIRES_AT, String(expiresAt));

    setUser(userData);
    return expiresAt;
  }, []);

  const login = useCallback(
    (userData, token) => {
      establishSession(userData, token);

      // Route based on role (admin vs rider vs default)
      console.log("Logging in user:", userData);
      if (userData?.isAdmin) navigate("/admin", { replace: true });
      else navigate("/rider", { replace: true });
    },
    [establishSession, navigate]
  );

  // Load persisted user on mount
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const token = localStorage.getItem(LS_TOKEN);
    const raw = localStorage.getItem(LS_USER);

    if (token && raw) {
      const parsed = safeJsonParse(raw);
      if (parsed) setUser(parsed);
      else clearSessionStorage();
    }

    setLoading(false);
  }, [clearSessionStorage]);

  // Track user activity to enforce IDLE timeout
  useEffect(() => {
    if (!user) return;

    const markActivity = () => {
      localStorage.setItem(LS_LAST_ACTIVITY, String(Date.now()));
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActivity));
    };
  }, [user]);

  // Enforce timeouts (idle + absolute/JWT exp)
  useEffect(() => {
    if (!user) return;

    const check = () => {
      const now = Date.now();
      const lastActivity = Number(localStorage.getItem(LS_LAST_ACTIVITY) || "0");
      const expiresAt = Number(localStorage.getItem(LS_EXPIRES_AT) || "0");

      const idleTooLong = lastActivity && now - lastActivity > IDLE_TIMEOUT_MS;
      const pastExpiry = expiresAt && now > expiresAt;

      if (idleTooLong || pastExpiry) {
        logout({ redirect: true });
      }
    };

    const id = setInterval(check, CHECK_INTERVAL_MS);
    check();

    return () => clearInterval(id);
  }, [user, logout]);

  const value = useMemo(
    () => ({
      user,
      setUser, // still exposed if you need it
      loading,
      login,
      logout,
      establishSession, // optional: for custom login flows
      // helpful for ProtectedRoute checks or UI
      getSessionExpiresAt: () => Number(localStorage.getItem(LS_EXPIRES_AT) || "0"),
    }),
    [user, loading, login, logout, establishSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
